// Copyright (c) 2020 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import type { JSX } from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, IntlContext, IntlShape } from "react-intl";
import { DateTime, Duration } from "luxon";

import { RoutedProps, withRouter } from "view/reactrouterinterceptor";
import { DataState, MainPanel } from "types";
import { DeletionStrategy } from "shared/types/cloudtypes";
import { ActionType, IAction, IActionSortTable, IActionTypeEdit, raiseAction } from "actions";
import { IState } from "states/states";
import { StopReason } from "model/connector";
import Rfid from "model/rfid";
import { IOcppCharger, IOcppChargingSession, IRfid, ITariff } from "states/ocppstates";
import { IPeriodData, ITableSort } from "states/guistates";
import { formatTimestamp, isSet, roundFix } from "shared/utils/utils";
import { isStateDirty, makeSortHeader, parseHtmlInputValue, tabClickHandler } from "guiutils";

import FormattedDate from "view/comp/FormattedDate";
import FormattedTime from "view/comp/FormattedTime";
import TrendHeading from "view/comp/TrendHeading";

// sort key of table
const TABLE_NAME = "t_chargingsessions";

interface IChargingSessionListExtProps {
  isElementSelected?: boolean;
}

interface IChargingSessionListProps {
  lastUpdate: number;
  isRfidUser: boolean;
  chargers: IOcppCharger[];
  chargingSession: IOcppChargingSession[];
  chargingSessionFilter: IPeriodData;
  knownRfids: string[];
  rfids: IRfid[];
  tariffs: ITariff[];
  dataState: DataState;
  tableSort: ITableSort | undefined;
  chargerId?: number | undefined;
  ocppChargers: IOcppCharger[];
  deletionStrategy: DeletionStrategy | undefined;
  filterText?: string;
  filteredCharger?: string;
  rfidFilter: string | undefined;
}

const mapStateToProps = (state: IState) => {
  // determine deletion strategy for charging session details
  // this might not fit for virtual sites as the main charger sites determine the deletion strategy
  // however, we accept this as this will anyway typically be equal
  const deletionStrategy = state.site.siteList?.find(
    (curSite) => curSite.id === state.cached.selectedSite,
  )?.cloudSettings?.deletionStrategy;

  const props: IChargingSessionListProps = {
    lastUpdate: state.scheduler.pendingOcppSessionState.lastData ?? 0,
    isRfidUser: !!state.site.user?.isRfidUser,
    chargers: state.ocpp.charger,
    chargingSession: state.ocpp.chargingSession,
    chargingSessionFilter: state.gui.chargingSessionFilter,
    knownRfids: state.gui.rfidList,
    rfids: state.ocpp.rfid,
    tariffs: state.ocpp.siteTariff,
    dataState: state.scheduler.ocppSessionState,
    // newest sessions first by default
    tableSort: state.gui.tableSort[TABLE_NAME] ?? {
      column: "startTimestamp",
      isSortAscending: false,
    },
    chargerId: undefined,
    ocppChargers: state.ocpp.charger,
    deletionStrategy,
    filterText: state.gui.searchFilterText,
    filteredCharger: state.gui.chargingSessionFilter.chargerName,
    // only show sessions of matching cards, if no single card is selected
    rfidFilter: isSet(state.router.rfid) ? undefined : state.router.rfidFilter,
  };

  return props;
};

interface IChargingSessionListDispatch {
  onItemChanged: (event: React.FormEvent<HTMLInputElement>) => void;
  onValueChange: (name: string, value: string | number | undefined) => void;
  onSort: (sort: IActionSortTable) => void;
}

const mapDispatchToProps = (
  dispatch: (action: IAction<any>) => void,
  ownProps: IChargingSessionListExtProps,
) => {
  const dispatchProps: IChargingSessionListDispatch = {
    onItemChanged: (event: React.FormEvent<HTMLInputElement>) => {
      const editPayload: IActionTypeEdit = {
        name: event.currentTarget.name,
        value: parseHtmlInputValue(event.currentTarget),
      };
      dispatch(raiseAction(ActionType.ACT_EDIT_VALUE, editPayload));
      dispatch(raiseAction(ActionType.ACT_GET_OCPP_SESSIONS_REQ));
    },
    onValueChange: (name: string, value: string | number | undefined) => {
      const editPayload: IActionTypeEdit = {
        name,
        value,
      };
      dispatch(raiseAction(ActionType.ACT_EDIT_VALUE, editPayload));
    },
    onSort: (sort: IActionSortTable) => {
      dispatch(raiseAction(ActionType.ACT_SORT_TABLE, sort));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  periodTitle: {
    id: "Charging Sessions",
  },
  stopReasonDisconnected: {
    id: "disconnected",
  },
  stopReasonRefused: {
    id: "refused",
  },
  stopReasonExpired: {
    id: "expired",
  },
  stopReasonOther: {
    id: "other",
  },
  msg_user_caption: {
    id: "Select Charger",
  },
  placeholderSearchField: {
    id: "Search for chargers...",
  },
  toolTipSearchField: {
    id: "Search for charger name, vendor or serial number. To search for multiple fields, separate search texts by a space!",
  },
});

class ChargingSessionListComponent extends React.Component<
  IChargingSessionListExtProps &
    IChargingSessionListProps &
    IChargingSessionListDispatch &
    RoutedProps
> {
  static contextType = IntlContext;
  declare context: IntlShape;

  private showUser = false;
  private showCosts = false;
  private showStopReason = false;
  private showCar = false;
  private showAdditionalData = false;

  // determine if detailed charging data (Ladeverlauf/trend data) is available
  // based on deletion strategy and session timestamp
  private hasChargingSessionDetails(sessionTimestamp: number | undefined): boolean {
    if (!isSet(sessionTimestamp)) {
      return false;
    }

    // determine deletion duration for charging details (Ladeverlauf)
    const deletionDuration: Duration | undefined =
      this.props.deletionStrategy === DeletionStrategy.SMALL ? Duration.fromObject({ months: 3 })
      : this.props.deletionStrategy === DeletionStrategy.LARGE ? Duration.fromObject({ months: 6 })
      : this.props.deletionStrategy === DeletionStrategy.UNLIMITED ?
        Duration.fromObject({ years: 1 })
      : undefined;

    if (!deletionDuration) {
      // no deletion strategy set, assume data is available
      return true;
    }
    // calculate deletion boundary (start of month)
    const deletionBoundary = DateTime.now().minus(deletionDuration).startOf("month");
    // convert session timestamp to DateTime
    const sessionDate = DateTime.fromSeconds(sessionTimestamp);
    // data is available if session is newer than deletion boundary
    return sessionDate >= deletionBoundary;
  }

  public render() {
    const filterTextsLowerCase = this.props.filterText?.toLocaleLowerCase().split(" ") ?? [];
    this.showUser =
      !this.props.isRfidUser && this.props.chargingSession.some((curItem) => isSet(curItem.rfid));
    this.showCar =
      !this.props.isRfidUser &&
      this.props.chargingSession.some((curSession) =>
        isSet(this.props.rfids.find((curRfid) => curSession.rfidId === curRfid.id)?.carName),
      );
    this.showAdditionalData =
      !this.props.isRfidUser &&
      this.props.chargingSession.some((curSession) =>
        isSet(this.props.rfids.find((curRfid) => curSession.rfidId === curRfid.id)?.additionalData),
      );
    this.showCosts = this.props.chargingSession.some((curItem) => isSet(curItem.fk_tariff));
    this.showStopReason = this.props.chargingSession.some((curItem) => isSet(curItem.stopReason));

    const filteredRfids =
      isSet(this.props.rfidFilter) ?
        this.props.rfids.filter(
          (curRfid) => curRfid.isActive && new Rfid(curRfid).matchesFilter(this.props.rfidFilter!),
        )
      : undefined;
    const sessions = this.getSortedSessions().filter((curItem) => {
      const charger = this.props.chargers.find(
        (searchItem) => searchItem.fk_charger === curItem.fk_charger,
      );
      // a Keba slave charger delivers rfid_charger as the rfid code
      const sessionRfid = curItem.rfid?.split("_")[0];
      const user = this.props.rfids.find(
        (curRfid) => curRfid.rfid === sessionRfid || curRfid.id === curItem.rfidId,
      );
      return (
        isSet(charger) &&
        (!isSet(filteredRfids) ||
          filteredRfids.some(
            (curRfid) => curRfid.id === curItem.rfidId || curRfid.rfid === sessionRfid,
          )) &&
        (!isSet(this.props.filteredCharger) ||
          charger.chargepoint === this.props.filteredCharger) &&
        filterTextsLowerCase.every(
          (curFilter) =>
            curFilter === "" ||
            charger.name?.toLocaleLowerCase().includes(curFilter) ||
            charger.chargepoint.toLocaleLowerCase().includes(curFilter) ||
            charger.serial?.toLocaleLowerCase().includes(curFilter) ||
            charger.vendor?.toLocaleLowerCase().includes(curFilter) ||
            sessionRfid?.toLocaleLowerCase().includes(curFilter) ||
            (isSet(user) && new Rfid(user).matchesFilter(curFilter)),
        )
      );
    });

    return (
      <div id="chargingsessionlist">
        {/* {!this.props.isRfidUser && this.renderFilter()} */}
        <div className="headerbuttons">
          <div className="left">
            <TrendHeading
              identifier="chargingSessionFilter"
              includeDate={!isStateDirty(this.props.dataState)}
              title={this.context.formatMessage(messages.periodTitle)}
            />
            <div className="searchfeedback">{this.renderSearchFeedback()}</div>
            {/* <div className="d-flex searchfilter">
              {this.props.isRfidUser && this.renderChargerFilter()}
            </div> */}
          </div>
        </div>

        <div id="sessiontable">
          {sessions.length === 0 ?
            <div className="emptytable pb-2">
              <FormattedMessage id="No charging sessions in selected period." />
            </div>
          : <table id={TABLE_NAME} className="datatable fullwidthwhite">
              <thead>
                <tr>
                  {this.makeSortHeader("startTimestamp", <FormattedMessage id="Start" />)}
                  {this.makeSortHeader("stopTimestamp", <FormattedMessage id="End" />, [
                    "optional-lg keep-print",
                  ])}
                  {this.makeSortHeader("fk_charger", <FormattedMessage id="Charger" />, [
                    "optional-lg",
                    "charger-col",
                  ])}
                  {this.showUser && this.makeSortHeader("rfid", <FormattedMessage id="User" />)}
                  {this.showAdditionalData &&
                    this.makeSortHeader(
                      "additionalData",
                      <FormattedMessage id="Additional Data" />,
                      ["optional-lg"],
                    )}
                  {this.showCar &&
                    this.makeSortHeader("carName", <FormattedMessage id="Vehicle" />, [
                      "optional-lg",
                    ])}
                  {this.makeSortHeader("startMeter", <FormattedMessage id="Duration" />, ["right"])}
                  {this.makeSortHeader("stopMeter", <FormattedMessage id="Energy" />, ["right"])}
                  {this.showCosts &&
                    this.makeSortHeader("costs", <FormattedMessage id="Costs" />, ["right"])}
                  {this.showCosts &&
                    this.makeSortHeader("fk_tariff", <FormattedMessage id="Tariff" />, [
                      "optional-xxl",
                    ])}
                  {this.showStopReason &&
                    this.makeSortHeader("stopReason", <FormattedMessage id="Stop Reason" />, [
                      "optional-xxl",
                    ])}
                </tr>
              </thead>
              <tbody>{sessions.map((curItem) => this.renderSession(curItem))}</tbody>
              {sessions.length > 1 && <tfoot>{this.renderSumRow(sessions)}</tfoot>}
            </table>
          }
        </div>
      </div>
    );
  }

  // this function is just a short-cut to reduce boilerplate
  private makeSortHeader(
    column: keyof IOcppChargingSession | "carName" | "additionalData",
    caption: JSX.Element,
    classes?: string[],
    title?: string,
  ) {
    return makeSortHeader(
      TABLE_NAME,
      column,
      caption,
      this.props.tableSort,
      this.props.onSort,
      classes,
      title,
    );
  }

  private getSortedSessions() {
    const sortCol = this.props.tableSort?.column as keyof IOcppChargingSession | undefined;
    // data state is immutable so we need to copy it before sorting
    return [...this.props.chargingSession].sort((a, b) => {
      let keyA: string | number | undefined;
      let keyB: string | number | undefined;
      keyA = a[sortCol!];
      keyB = b[sortCol!];
      // special handling for special columns
      if (sortCol === "rfid") {
        keyA = this.getUsername(keyA as string, undefined);
        keyB = this.getUsername(keyB as string, undefined);
      } else if (sortCol === "fk_charger") {
        const chargerA = this.props.chargers.find((cur) => cur.fk_charger === a.fk_charger);
        keyA =
          a.rfid?.includes("_") ?
            a.rfid.split("_").pop()
          : (chargerA?.name ?? chargerA?.chargepoint);
        const chargerB = this.props.chargers.find((cur) => cur.fk_charger === b.fk_charger);
        keyB =
          b.rfid?.includes("_") ?
            b.rfid.split("_").pop()
          : (chargerB?.name ?? chargerB?.chargepoint);
      } else if (sortCol === "startMeter") {
        // column is misused as "duration"
        keyA = (a.stopTimestamp ?? 0) - a.startTimestamp;
        keyB = (b.stopTimestamp ?? 0) - b.startTimestamp;
      } else if (sortCol === "stopMeter") {
        // column is misused as "energy"
        keyA = (a.stopMeter ?? a.startMeter) - a.startMeter;
        keyB = (b.stopMeter ?? b.startMeter) - b.startMeter;
      } else if (sortCol === "stopReason") {
        keyA = this.getStopReasonString(a.stopReason);
        keyB = this.getStopReasonString(b.stopReason);
      } else if (sortCol === "fk_tariff") {
        keyA = this.props.tariffs.find((curItem) => curItem.id === keyA)?.name ?? keyA;
        keyB = this.props.tariffs.find((curItem) => curItem.id === keyB)?.name ?? keyB;
      }
      const sort =
        isSet(keyA) && isSet(keyB) ?
          keyA > keyB ?
            1
          : -1
        : isSet(keyB) ? 1
        : -1;
      return this.props.tableSort?.isSortAscending !== false ? sort : -sort;
    });
  }

  private renderFilter() {
    // text selection hint includes all rfids and names that we know
    const allRfids = [...this.props.knownRfids]; // clone this as the state is immutable
    allRfids.push(...this.props.rfids.map((curItem) => curItem.rfid));
    allRfids.push(
      ...this.props.rfids.filter((curItem) => curItem.name).map((curItem) => curItem.name!),
    );
    const datalist = [...new Set(allRfids)].sort().map((curItem) => {
      return (
        <option key={curItem} value={curItem}>
          {curItem}
        </option>
      );
    });

    return (
      <div id="filter" className="headerbuttons">
        <div className="left chargerFilter align-items-center">
          <div>
            <FormattedMessage id="User Filter" />:
          </div>
          <input
            title={this.context.formatMessage(messages.toolTipSearchField)}
            name="chargingSessionFilter.user"
            list="chargingSessionFilter.user"
            type="text"
            className="ms-1"
            size={33}
            value={this.props.chargingSessionFilter.user ?? ""}
            onChange={this.props.onItemChanged}
          ></input>
          <datalist id="chargingSessionFilter.rfid">{datalist}</datalist>
        </div>
      </div>
    );
  }

  private renderSearchFeedback() {
    return (
      <>
        {this.props.dataState === DataState.SAVE_REQ ?
          <div className="ms-2 alert alert-warning">
            <FormattedMessage id="refreshing list..." />
          </div>
        : this.props.dataState === DataState.SAVE_FAIL ?
          <div className="ms-2 alert alert-danger">
            <FormattedMessage id="Search failed." />
          </div>
        : <></>}
      </>
    );
  }

  private getUsername(rfid: string | undefined, rfidId: number | undefined) {
    // look, whether we have an rfid card with a name associated to this entry
    const user = this.props.rfids.find((curItem) => curItem.rfid === rfid || curItem.id === rfidId);
    if (user && user.category) {
      return `${user.name} (${user.category})`;
    }
    return user?.name ?? rfid;
  }

  private renderSession(data: IOcppChargingSession) {
    const charger = this.props.chargers.find((curItem) => curItem.fk_charger === data.fk_charger);
    // a Keba slave charger delivers rfid_charger as the rfid code
    const rfid = data.rfid?.includes("_") ? data.rfid.split("_")[0] : data.rfid;
    const chargerName =
      data.rfid?.includes("_") ? data.rfid.split("_").pop()
      : isSet(charger) ? (charger.name ?? charger.chargepoint)
      : <FormattedMessage id="RESTRICTED CHARGER" />;
    const hasSessionDetails = this.hasChargingSessionDetails(data.startTimestamp);
    const carName =
      this.showCar ?
        this.props.rfids.find((curItem) => curItem.id === data.rfidId)?.carName
      : undefined;
    const additionalData =
      this.showAdditionalData ?
        this.props.rfids.find((curItem) => curItem.id === data.rfidId)?.additionalData
      : undefined;
    const tariff = this.props.tariffs.find((curItem) => curItem.id === data.fk_tariff);

    return (
      <tr
        key={data.id}
        className={hasSessionDetails ? "link" : undefined}
        tabIndex={hasSessionDetails ? 0 : undefined}
        onKeyDown={hasSessionDetails ? tabClickHandler : undefined}
        onClick={
          hasSessionDetails ?
            () => this.props.navigate(`../${MainPanel.CHARGINGSESSION}?transaction=${data.id}`)
          : undefined
        }
      >
        <td>
          <FormattedDate valueMs={data.startTimestamp * 1000} doZeroPadding={true} />{" "}
          <FormattedTime valueMs={data.startTimestamp * 1000} doZeroPadding={true} />
        </td>
        <td className="optional-lg keep-print">
          {data.stopTimestamp ?
            <>
              <FormattedDate valueMs={data.stopTimestamp * 1000} doZeroPadding={true} />{" "}
              <FormattedTime valueMs={data.stopTimestamp * 1000} doZeroPadding={true} />
            </>
          : <FormattedMessage id="ongoing" />}
        </td>
        <td className="optional-lg charger-col">{chargerName}</td>
        {this.showUser && (
          <td>
            {this.getUsername(rfid, data.rfidId)}
            {/* several cards may share a name; show the card number to distinguish them */}
            {isSet(this.props.rfidFilter) && this.getUsername(rfid, data.rfidId) !== rfid && (
              <div className="rfid-number">{rfid}</div>
            )}
          </td>
        )}
        {this.showAdditionalData && <td className="optional-lg">{additionalData}</td>}
        {this.showCar && <td className="optional-lg">{carName}</td>}
        <td className="right nowrap">
          {data.stopTimestamp && formatTimestamp(data.stopTimestamp - data.startTimestamp)}
        </td>
        <td className="right nowrap">
          {data.stopMeter && `${roundFix((data.stopMeter - data.startMeter) / 1000, 1)} kWh`}
        </td>
        {this.showCosts && (
          <td className="right nowrap">
            {isSet(data.costs) && `${roundFix(data.costs, 2)} ${tariff?.currency ?? "EUR"}`}
          </td>
        )}
        {this.showCosts && <td className="optional-xxl">{tariff?.name}</td>}
        {this.showStopReason && (
          <td className="optional-xxl">
            {data.stopTimestamp && this.getStopReasonString(data.stopReason)}
          </td>
        )}
      </tr>
    );
  }

  private getStopReasonString(stopReason: StopReason | undefined) {
    return this.context.formatMessage({ id: `StopReason_${stopReason ?? ""}` });
  }

  private renderSumRow(sessions: IOcppChargingSession[]) {
    // derive sums
    const energy = sessions.reduce((prevValue, curItem) => {
      if (curItem.stopMeter) {
        return prevValue + (curItem.stopMeter - curItem.startMeter) / 1000; // kWh
      }
      return prevValue; // do not add unfinished charging session
    }, 0);
    const duration = sessions.reduce((prevValue, curItem) => {
      if (curItem.stopTimestamp) {
        return prevValue + (curItem.stopTimestamp - curItem.startTimestamp); // s
      }
      return prevValue; // do not add unfinished charging session
    }, 0);
    const costs =
      this.showCosts ?
        sessions.reduce((prevValue, curItem) => {
          return prevValue + (curItem.costs ?? 0);
        }, 0)
      : 0;
    const tariff =
      this.showCosts ?
        this.props.tariffs.find((curItem) => curItem.id === sessions[0]?.fk_tariff)
      : undefined;

    return (
      <tr>
        <th>
          <FormattedMessage id="Sum" />
        </th>
        <th className="optional-lg keep-print"></th>
        <th className="optional-lg charger-col"></th>
        {this.showUser && <th></th>}
        {this.showAdditionalData && <th className="optional-lg"></th>}
        {this.showCar && <th className="optional-lg"></th>}
        <th className="right nowrap">{formatTimestamp(duration)}</th>
        <th className="right nowrap">{roundFix(energy, 1)} kWh</th>
        {this.showCosts && (
          <th className="right nowrap">
            {roundFix(costs, 2)} {tariff?.currency ?? "EUR"}
          </th>
        )}
        {this.showCosts && <th className="optional-xxl"></th>}
        {this.showStopReason && <th className="optional-xxl"></th>}
      </tr>
    );
  }
}

const ChargingSessions = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(ChargingSessionListComponent));

export default ChargingSessions;
